import {
  AfterContentInit,
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { StepDialogComponent, StepHeaderMode } from './step-dialog.component';

export type StepTemplateKey = number | string;

/**
 * Example template usage:
 *
 * <app-step-dialog-shell
 *   [controller]="this"
 *   mode="tabs"
 *   title="Add/Edit Benefits for Engineering Team"
 *   subtitle="Select the benefit plans and reimbursement categories."
 *   [showClose]="true"
 *   primaryLabel="Save Benefits"
 *   (closeClicked)="close()"
 *   (cancelClicked)="close()"
 *   (primaryClicked)="onSave()"
 * >
 *   <!-- by step id -->
 *   <ng-template [stepDialogStep]="'health'"> ... </ng-template>
 *   <ng-template [stepDialogStep]="'supplemental'"> ... </ng-template>
 *
 *   <!-- OR by index -->
 *   <ng-template [stepDialogStep]="0"> ... </ng-template>
 *
 *   <!-- optional per-step footer -->
 *   <ng-template [stepDialogFooter]="'health'"> ... </ng-template>
 *
 *   <!-- optional default footer override -->
 *   <ng-template stepDialogFooterDefault> ... </ng-template>
 * </app-step-dialog-shell>
 *
 */

/**
 * Provide a step body template.
 * Usage:
 *  <ng-template [stepDialogStep]="0">...</ng-template>
 *  <ng-template [stepDialogStep]="'benefits'">...</ng-template>
 */
@Directive({
  selector: 'ng-template[stepDialogStep]',
})
export class StepDialogStepTemplateDirective {
  @Input('stepDialogStep') key!: StepTemplateKey;

  constructor(public templateRef: TemplateRef<unknown>) {}
}

/**
 * Provide a per-step footer template.
 * Usage:
 *  <ng-template [stepDialogFooter]="0">...</ng-template>
 *  <ng-template [stepDialogFooter]="'benefits'">...</ng-template>
 */
@Directive({
  selector: 'ng-template[stepDialogFooter]',
})
export class StepDialogFooterTemplateDirective {
  @Input('stepDialogFooter') key!: StepTemplateKey;

  constructor(public templateRef: TemplateRef<unknown>) {}
}

/**
 * Provide a default footer template (used when a per-step footer is not provided).
 * Usage:
 *  <ng-template stepDialogFooterDefault>...</ng-template>
 */
@Directive({
  selector: 'ng-template[stepDialogFooterDefault]',
})
export class StepDialogFooterDefaultTemplateDirective {
  constructor(public templateRef: TemplateRef<unknown>) {}
}

@Component({
  selector: 'app-step-dialog-shell',
  templateUrl: './step-dialog-shell.component.html',
  styleUrls: ['./step-dialog-shell.component.scss'],
})
export class StepDialogShellComponent implements AfterContentInit {
  /** The dialog controller instance (usually the dialog component itself). */
  @Input() controller!: StepDialogComponent;

  /** Optional override. If not set, uses controller.headerMode. */
  @Input() mode?: StepHeaderMode;

  /** Optional title row text. */
  @Input() title = '';

  /** Optional subtitle text under the title (matches tab-based design). */
  @Input() subtitle = '';

  /** Optional header icon (Mat icon name). */
  @Input() headerIcon = 'group';

  /** Optional explicit toggle for header icon visibility. Defaults by mode. */
  @Input() showHeaderIcon?: boolean;

  /** Optional close icon in the top-right. */
  @Input() showClose = false;
  @Input() disableClose = false;

  /** Tab-footer button labels (only used for mode === 'tabs' in the default footer). */
  @Input() previousLabel = 'Previous';
  @Input() nextLabel = 'Next';
  @Input() cancelLabel = 'Cancel';
  @Input() primaryLabel = 'Save';

  /** Tab-footer button visibility. */
  @Input() showCancel = true;
  @Input() showPrimary = true;

  /** Tab-footer button disabled states. */
  @Input() disablePrimary = false;
  @Input() disableCancel = false;

  /** Emits when the close icon is clicked. */
  @Output() closeClicked = new EventEmitter<void>();

  /** Emits when the Cancel button is clicked (default tab footer). */
  @Output() cancelClicked = new EventEmitter<void>();

  /** Emits when the Primary action button is clicked (default tab footer). */
  @Output() primaryClicked = new EventEmitter<void>();

  // Step & footer templates (arbitrary count)
  @ContentChildren(StepDialogStepTemplateDirective) private stepTemplates!: QueryList<StepDialogStepTemplateDirective>;
  @ContentChildren(StepDialogFooterTemplateDirective) private footerTemplates!: QueryList<StepDialogFooterTemplateDirective>;
  @ContentChildren(StepDialogFooterDefaultTemplateDirective)
  private footerDefaultTemplates!: QueryList<StepDialogFooterDefaultTemplateDirective>;

  private stepTemplateMap = new Map<StepTemplateKey, TemplateRef<unknown>>();
  private footerTemplateMap = new Map<StepTemplateKey, TemplateRef<unknown>>();
  private footerDefaultTemplate?: TemplateRef<unknown>;

  ngAfterContentInit(): void {
    this.rebuildTemplateMaps();

    this.stepTemplates.changes.subscribe(() => {
      this.rebuildTemplateMaps();
    });

    this.footerTemplates.changes.subscribe(() => {
      this.rebuildTemplateMaps();
    });

    this.footerDefaultTemplates.changes.subscribe(() => {
      this.rebuildTemplateMaps();
    });
  }

  private rebuildTemplateMaps(): void {
    this.stepTemplateMap.clear();
    this.footerTemplateMap.clear();

    for (const step of this.stepTemplates ?? []) {
      this.stepTemplateMap.set(step.key, step.templateRef);
    }

    for (const footer of this.footerTemplates ?? []) {
      this.footerTemplateMap.set(footer.key, footer.templateRef);
    }

    this.footerDefaultTemplate = this.footerDefaultTemplates?.first?.templateRef;
  }

  get headerMode(): StepHeaderMode {
    return this.mode ?? this.controller.headerMode;
  }

  get shouldShowHeaderIcon(): boolean {
    if (this.showHeaderIcon !== undefined) {
      return this.showHeaderIcon;
    }
    return this.headerMode === 'sequential' && !!this.title;
  }

  get hasHeaderText(): boolean {
    return !!this.title || !!this.subtitle;
  }

  get shouldRenderHeader(): boolean {
    return this.hasHeaderText || this.showClose || this.shouldShowHeaderIcon;
  }

  get shouldRenderHeaderDivider(): boolean {
    return this.headerMode === 'sequential' || this.headerMode === 'tabs';
  }

  isStepComplete(index: number): boolean {
    return this.controller.getStepState(index) === 'complete';
  }

  isStepLocked(index: number): boolean {
    if (this.headerMode === 'tabs') {
      return false;
    }
    return !this.controller.canNavigateToStep(index);
  }

  onStepSelect(index: number): void {
    if (index < 0 || index >= (this.controller.steps?.length ?? 0)) {
      return;
    }

    if (this.headerMode === 'tabs') {
      this.controller.stepIndex = index;
      return;
    }

    this.controller.goToStep(index);
  }

  private getCurrentStepKey(): StepTemplateKey {
    const step = this.controller.steps?.[this.controller.stepIndex];
    if (step?.id) {
      return step.id;
    }
    return this.controller.stepIndex;
  }

  get currentStepTemplate(): TemplateRef<unknown> | undefined {
    const key = this.getCurrentStepKey();
    return this.stepTemplateMap.get(key) ?? this.stepTemplateMap.get(this.controller.stepIndex);
  }

  get currentFooterTemplate(): TemplateRef<unknown> | undefined {
    const key = this.getCurrentStepKey();
    return (
      this.footerTemplateMap.get(key) ??
      this.footerTemplateMap.get(this.controller.stepIndex) ??
      this.footerDefaultTemplate
    );
  }

  onCloseClick(): void {
    this.closeClicked.emit();
  }

  onCancelClick(): void {
    this.cancelClicked.emit();
  }

  onPrimaryClick(): void {
    this.primaryClicked.emit();
  }
}
